import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@material-ui/core";
import Extension from "@material-ui/icons/Extension";
import SmartToy from "@mui/icons-material/SmartToy";
import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import { useChannelPlugins } from "../components/ChannelIcon";
import { app as appUI, openHtmlModal } from "@markedesk/plugin-sdk/ui";
import PluginHtmlInline from "../components/PluginBridge/PluginHtmlInline";
import PluginJsxInline from "../components/PluginBridge/PluginJsxInline";
import api from "../services/api";

const iconMap = { Extension, SmartToy, AutoFixHigh };

/** Caminho da página inserida de uma action (`openType: "page"`). */
export function pluginPagePath(pluginId, actionId) {
  return `/plugin/${encodeURIComponent(pluginId)}/${encodeURIComponent(actionId)}`;
}

/**
 * Itens de menu lateral declarados por plugins (slot "sidebar-menu").
 *
 * O ITEM: por padrão ícone + rótulo (ícone do mapa fechado). Com
 * `render: "jsx"` + `jsx` o plugin desenha o item inteiro em React do app
 * (recebe `props.open()` para abrir e `props.collapsed`); com `render: "html"`
 * + `html`, HTML próprio com o clique tratado aqui.
 *
 * O CLIQUE, em ordem:
 *  - `openType: "page"` → navega para a PÁGINA INSERIDA (`/plugin/:id/:action`,
 *    dentro do layout, na sessão — `pages/PluginPage`);
 *  - `resultType: "jsx"` + `resultJsx` → modal React do app (mesmo motor de
 *    `openModal({ jsx })`); com `endpoint`, faz POST antes e entrega
 *    `props.dados`;
 *  - senão → iframe (`routePath + endpoint`), página inteira servida pelo
 *    plugin, fora da sessão.
 *
 * Até set/2026 só existia o iframe. Renderiza nada se ninguém declarar o slot.
 */
export default function PluginSidebarItems({ collapsed }) {
  const plugins = useChannelPlugins();
  const history = useHistory();
  const [panel, setPanel] = useState(null); // { title, src }

  const items = useMemo(() => {
    const lista = [];
    for (const plugin of plugins) {
      for (const action of plugin.provides?.actions || []) {
        if (action.slot === appUI.slots.SIDEBAR_MENU) lista.push({ ...action, plugin });
      }
    }
    return lista;
  }, [plugins]);

  if (items.length === 0) return null;

  const endpointOf = (action) => {
    if (!action.endpoint) return "";
    return action.endpoint.startsWith("/") ? action.endpoint : `/${action.endpoint}`;
  };

  const pluginApiOf = (routePath) => ({
    get: (caminho, config) => api.get(`${routePath || ""}${caminho}`, config),
    post: (caminho, corpo, config) => api.post(`${routePath || ""}${caminho}`, corpo, config),
  });

  const openJsxModal = async (action) => {
    const routePath = action.plugin.routePath;
    const ep = endpointOf(action);
    let dados = null;
    if (routePath && ep) {
      try {
        const res = await api.post(`${routePath}${ep}`, { pluginId: action.plugin.id, actionId: action.id });
        dados = res?.data ?? null;
      } catch (e) {
        dados = { erro: String(e?.message || e) };
      }
    }
    openHtmlModal({
      title: action.label,
      width: action.width || "lg",
      jsx: action.resultJsx,
      props: { dados, routePath, api: pluginApiOf(routePath), pluginId: action.plugin.id, actionId: action.id },
    });
  };

  const openPanel = (action) => {
    if (action.openType === "page") {
      history.push(pluginPagePath(action.plugin.id, action.id));
      return;
    }
    if (action.resultType === "jsx" && action.resultJsx) {
      openJsxModal(action);
      return;
    }
    if (!action.plugin.routePath) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    setPanel({ title: action.label, src: `${backendUrl}${action.plugin.routePath}${endpointOf(action)}` });
  };

  const renderItem = (action) => {
    const key = `${action.plugin.id}-${action.id}`;
    const routePath = action.plugin.routePath;

    if (action.render === "jsx" && action.jsx) {
      return (
        <PluginJsxInline
          key={key}
          block
          jsx={action.jsx}
          props={{
            collapsed: !!collapsed,
            open: () => openPanel(action),
            label: action.label,
            routePath,
            api: pluginApiOf(routePath),
            pluginId: action.plugin.id,
            actionId: action.id,
          }}
        />
      );
    }

    if (action.render === "html") {
      return (
        <div key={key} role="button" tabIndex={0} onClick={() => openPanel(action)} onKeyDown={(e) => { if (e.key === "Enter") openPanel(action); }}>
          <PluginHtmlInline block routePath={routePath} endpoint={action.endpoint ? undefined : `/${action.id}`} html={action.html} context={{ collapsed: !!collapsed }} />
        </div>
      );
    }

    const IconComp = iconMap[action.icon] || Extension;
    return (
      <ListItem button key={key} onClick={() => openPanel(action)}>
        <ListItemIcon><IconComp /></ListItemIcon>
        {!collapsed && <ListItemText primary={action.label} />}
      </ListItem>
    );
  };

  return (
    <>
      {items.map(renderItem)}

      <Dialog open={!!panel} onClose={() => setPanel(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{panel?.title}</DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {panel && (
            <iframe
              title={panel.title || "Plugin"}
              src={panel.src}
              style={{ width: "100%", height: "70vh", border: 0, display: "block" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPanel(null)} color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
